<template>
  <div class="business-report">
    <header class="page-header">
      <button class="btn-back" @click="$router.go(-1)">←</button>
      <h1>{{ $t('wholesaler.report.BusinessReport.line5_text') }}</h1>
      <button class="btn-period" @click="showPeriodPicker = true">{{ periodLabel }}</button>
    </header>

    <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>

    <div v-else class="report-content">
      <!-- 统计卡片 -->
      <div class="stats-row">
        <div class="stat-card primary">
          <div class="stat-label">{{ $t('wholesaler.report.BusinessReport.line15_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(report.totalSales) }}</div>
        </div>
        <div class="stat-card success">
          <div class="stat-label">{{ $t('wholesaler.report.BusinessReport.line19_text') }}</div>
          <div class="stat-value">{{ report.orderCount }}</div>
        </div>
      </div>
      <div class="stats-row">
        <div class="stat-card info">
          <div class="stat-label">{{ $t('wholesaler.report.BusinessReport.line25_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(report.avgOrderAmount) }}</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-label">{{ $t('wholesaler.report.BusinessReport.line29_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(report.paidAmount) }}</div>
        </div>
      </div>
      <div class="stats-row single">
        <div class="stat-card danger">
          <div class="stat-label">{{ $t('wholesaler.report.BusinessReport.line35_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(report.unpaidAmount) }}</div>
        </div>
      </div>

      <!-- 销售趋势（纯 CSS 柱状图） -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line42_text') }}</div>
        <div v-if="!report.trend || report.trend.length === 0" class="empty">{{ $t('common.noData') }}</div>
        <div v-else class="trend-chart">
          <div class="trend-bars">
            <div v-for="item in report.trend" :key="item.date" class="trend-bar-col">
              <div class="trend-bar-value">¥{{ formatMoneyShort(item.sales) }}</div>
              <div class="trend-bar-wrapper">
                <div class="trend-bar" :style="{ height: getTrendPercent(item.sales) + '%' }"></div>
              </div>
              <div class="trend-bar-label">{{ item.date.slice(5) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 商品排行 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line59_text') }}</div>
        <div v-if="!report.topProducts || report.topProducts.length === 0" class="empty">{{ $t('common.noData') }}</div>
        <div v-for="(item, index) in report.topProducts" :key="item.productId" class="rank-item">
          <span class="rank-number" :class="'top-' + (index + 1)">{{ index + 1 }}</span>
          <div class="rank-info">
            <div class="rank-name">{{ item.productName }}</div>
            <div class="rank-detail">销量: {{ item.quantity }}</div>
          </div>
          <div class="rank-amount">¥{{ formatMoney(item.amount) }}</div>
        </div>
      </div>

      <!-- 客户排行 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line73_text') }}</div>
        <div v-if="!report.topCustomers || report.topCustomers.length === 0" class="empty">{{ $t('common.noData') }}</div>
        <div v-for="(item, index) in report.topCustomers" :key="item.customerId" class="rank-item">
          <span class="rank-number" :class="'top-' + (index + 1)">{{ index + 1 }}</span>
          <div class="rank-info">
            <div class="rank-name">{{ item.customerName }}</div>
            <div class="rank-detail">订单: {{ item.orderCount }}</div>
          </div>
          <div class="rank-amount">¥{{ formatMoney(item.totalAmount) }}</div>
        </div>
      </div>

      <!-- 支付方式分布 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line87_text') }}</div>
        <div v-if="!report.paymentMethods || Object.keys(report.paymentMethods).length === 0" class="empty">{{ $t('common.noData') }}</div>
        <div v-for="(amount, method) in report.paymentMethods" :key="method" class="dist-item">
          <span class="dist-label">{{ method }}</span>
          <div class="dist-bar">
            <div class="dist-bar-fill" :style="{ width: getPaymentPercent(amount) + '%' }"></div>
          </div>
          <span class="dist-amount">¥{{ formatMoney(amount) }}</span>
        </div>
      </div>

      <!-- 订单状态分布 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line100_text') }}</div>
        <div v-if="!report.orderStatusDistribution || Object.keys(report.orderStatusDistribution).length === 0" class="empty">{{ $t('common.noData') }}</div>
        <div v-for="(count, status) in report.orderStatusDistribution" :key="status" class="status-item">
          <span class="status-label">{{ getStatusLabel(status) }}</span>
          <span class="status-count">{{ count }} 单</span>
        </div>
      </div>
    </div>

    <!-- 时间选择弹窗 -->
    <div v-if="showPeriodPicker" class="modal-overlay" @click.self="showPeriodPicker = false">
      <div class="modal-content">
        <div class="modal-header">
          <button class="btn-back" @click="showPeriodPicker = false">←</button>
          <h3>{{ $t('wholesaler.report.BusinessReport.line114_text') }}</h3>
          <div style="width: 32px;"></div>
        </div>
        <div class="modal-body">
          <div class="period-options">
            <button class="period-option" :class="{ active: period === 'day' }" @click="selectPeriod('day')">{{ $t('wholesaler.report.BusinessReport.line119_text') }}</button>
            <button class="period-option" :class="{ active: period === 'week' }" @click="selectPeriod('week')">{{ $t('wholesaler.report.BusinessReport.line120_text') }}</button>
            <button class="period-option" :class="{ active: period === 'month' }" @click="selectPeriod('month')">{{ $t('wholesaler.report.BusinessReport.line121_text') }}</button>
            <button class="period-option" :class="{ active: period === 'year' }" @click="selectPeriod('year')">{{ $t('wholesaler.report.BusinessReport.line122_text') }}</button>
          </div>
          <div class="custom-date-section">
            <div class="section-title">{{ $t('wholesaler.report.BusinessReport.line125_text') }}</div>
            <div class="date-input-group">
              <label>{{ $t('wholesaler.report.BusinessReport.line127_text') }}</label>
              <input type="date" v-model="startDate" />
            </div>
            <div class="date-input-group">
              <label>{{ $t('wholesaler.report.BusinessReport.line131_text') }}</label>
              <input type="date" v-model="endDate" />
            </div>
            <button class="btn-primary" @click="selectCustomPeriod" :disabled="!startDate || !endDate">{{ $t('common.confirm') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.report.BusinessReport.line152_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getBusinessReport } from '@/api/report'

export default {
  name: 'BusinessReport',
  
  data() {
    return {
      loading: false,
      period: 'month',
      startDate: '',
      endDate: '',
      showPeriodPicker: false,
      report: {
        totalSales: 0,
        orderCount: 0,
        avgOrderAmount: 0,
        paidAmount: 0,
        unpaidAmount: 0,
        topProducts: [],
        topCustomers: [],
        paymentMethods: {},
        trend: [],
        orderStatusDistribution: {}
      }
    }
  },

  computed: {
    periodLabel() {
      const labels = { day: '今日', week: '本周', month: '本月', year: '本年', custom: '自定义' }
      return labels[this.period] || '本月'
    }
  },

  mounted() {
    this.loadReport()
  },

  methods: {
    async loadReport() {
      this.loading = true
      try {
        const params = { period: this.period }
        if (this.period === 'custom' && this.startDate && this.endDate) {
          params.startDate = this.startDate
          params.endDate = this.endDate
        }
        const res = await getBusinessReport(params)
        if (res && res.success) {
          this.report = res.data || this.report
        }
      } catch (error) {
        console.error('加载营业报表失败:', error)
        alert('加载报表失败')
      } finally {
        this.loading = false
      }
    },

    selectPeriod(period) {
      this.period = period
      this.showPeriodPicker = false
      this.loadReport()
    },

    selectCustomPeriod() {
      if (this.startDate && this.endDate) {
        this.period = 'custom'
        this.showPeriodPicker = false
        this.loadReport()
      }
    },

    formatMoney(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    formatMoneyShort(value) {
      if (value === null || value === undefined) return '0'
      if (value >= 10000) return (value / 10000).toFixed(1) + 'w'
      if (value >= 1000) return (value / 1000).toFixed(1) + 'k'
      return Number(value).toFixed(0)
    },

    getTrendPercent(sales) {
      if (!this.report.trend || this.report.trend.length === 0) return 0
      const max = Math.max(...this.report.trend.map(t => t.sales || 0))
      if (max === 0) return 0
      return Math.max(((sales / max) * 100), 2).toFixed(1)
    },

    getPaymentPercent(amount) {
      const total = Object.values(this.report.paymentMethods).reduce((sum, v) => sum + v, 0)
      if (total === 0) return 0
      return (amount / total * 100).toFixed(1)
    },

    getStatusLabel(status) {
      const labels = {
        'PENDING': '待处理', 'CONFIRMED': '已确认', 'PACKING': '打包中',
        'READY_TO_SHIP': '待发货', 'DISPATCHED': '配送中', 'DELIVERED': '已送达',
        'COMPLETED': '已完成', 'CANCELLED': '已取消'
      }
      return labels[status] || status
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }

.business-report {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
  max-width: 480px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 100;
}

.page-header h1 { margin: 0; font-size: 17px; color: #333; font-weight: 600; }

.btn-back {
  background: none; border: none; font-size: 20px; cursor: pointer; color: #333; padding: 4px 8px;
}

.btn-period {
  background: #409EFF; color: white; border: none;
  padding: 6px 12px; border-radius: 16px; font-size: 13px; cursor: pointer;
}

.loading { text-align: center; padding: 60px 20px; color: #999; font-size: 15px; }

.report-content { padding: 12px; }

/* 统计卡片 */
.stats-row { display: flex; gap: 10px; margin-bottom: 10px; }
.stats-row.single { justify-content: center; }

.stat-card {
  flex: 1; padding: 12px; background: white; border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06); border-left: 3px solid;
}
.stat-card.primary { border-left-color: #409EFF; }
.stat-card.success { border-left-color: #67C23A; }
.stat-card.info { border-left-color: #909399; }
.stat-card.warning { border-left-color: #E6A23C; }
.stat-card.danger { border-left-color: #F56C6C; }

.stat-label { font-size: 12px; color: #999; margin-bottom: 4px; }
.stat-value { font-size: 18px; font-weight: 700; color: #333; }

/* 区块卡片 */
.section-card {
  background: white; border-radius: 10px; padding: 14px;
  margin-bottom: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.section-title { font-size: 15px; font-weight: 600; color: #333; margin-bottom: 12px; }

/* 趋势柱状图 */
.trend-chart { overflow-x: auto; }
.trend-bars { display: flex; gap: 4px; min-height: 120px; align-items: flex-end; }
.trend-bar-col { display: flex; flex-direction: column; align-items: center; min-width: 32px; flex: 1; }
.trend-bar-value { font-size: 9px; color: #999; margin-bottom: 2px; white-space: nowrap; }
.trend-bar-wrapper { width: 100%; height: 80px; display: flex; align-items: flex-end; justify-content: center; }
.trend-bar {
  width: 80%; max-width: 24px; background: linear-gradient(180deg, #409EFF, #79bbff);
  border-radius: 4px 4px 0 0; transition: height 0.3s; min-height: 2px;
}
.trend-bar-label { font-size: 9px; color: #999; margin-top: 4px; white-space: nowrap; }

/* 排行项 */
.rank-item { display: flex; align-items: center; padding: 10px 0; border-bottom: 1px solid #f0f0f0; }
.rank-item:last-child { border-bottom: none; }

.rank-number {
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  border-radius: 50%; font-size: 12px; font-weight: 600;
  background: #e4e7ed; color: #909399; margin-right: 10px; flex-shrink: 0;
}
.rank-number.top-1 { background: #FFD700; color: white; }
.rank-number.top-2 { background: #C0C0C0; color: white; }
.rank-number.top-3 { background: #CD7F32; color: white; }

.rank-info { flex: 1; min-width: 0; }
.rank-name { font-size: 14px; color: #333; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rank-detail { font-size: 11px; color: #999; margin-top: 2px; }
.rank-amount { font-size: 14px; font-weight: 600; color: #F56C6C; margin-left: 10px; flex-shrink: 0; }

/* 支付方式分布 */
.dist-item { display: flex; align-items: center; padding: 8px 0; }
.dist-label { width: 70px; font-size: 13px; color: #606266; flex-shrink: 0; }
.dist-bar { flex: 1; height: 16px; background: #f0f0f0; border-radius: 8px; overflow: hidden; margin: 0 10px; }
.dist-bar-fill { height: 100%; background: linear-gradient(90deg, #409EFF, #67C23A); transition: width 0.3s; }
.dist-amount { width: 80px; text-align: right; font-size: 13px; font-weight: 600; color: #333; flex-shrink: 0; }

/* 订单状态 */
.status-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #f0f0f0; }
.status-item:last-child { border-bottom: none; }
.status-label { font-size: 14px; color: #606266; }
.status-count { font-size: 14px; font-weight: 600; color: #409EFF; }

.empty { text-align: center; padding: 30px 20px; color: #999; font-size: 14px; }

/* 弹窗 */
.modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 1000;
  display: flex; align-items: flex-end; justify-content: center;
}
.modal-content {
  background: white; width: 100%; max-width: 480px; max-height: 80vh;
  border-radius: 16px 16px 0 0; overflow: hidden; display: flex; flex-direction: column;
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; border-bottom: 1px solid #eee; flex-shrink: 0;
}
.modal-header h3 { margin: 0; font-size: 16px; color: #333; font-weight: 600; }
.modal-body { padding: 16px; overflow-y: auto; flex: 1; }

.period-options { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
.period-option {
  padding: 12px; border: 1px solid #e4e7ed; border-radius: 8px;
  background: white; font-size: 14px; color: #606266; cursor: pointer;
}
.period-option.active { background: #409EFF; color: white; border-color: #409EFF; }

.custom-date-section { border-top: 1px solid #eee; padding-top: 16px; }
.date-input-group { margin-bottom: 12px; }
.date-input-group label { display: block; font-size: 13px; color: #606266; margin-bottom: 6px; }
.date-input-group input { width: 100%; padding: 10px 12px; border: 1px solid #e4e7ed; border-radius: 8px; font-size: 14px; }

.btn-primary {
  width: 100%; padding: 12px; background: #409EFF; color: white;
  border: none; border-radius: 8px; font-size: 15px; font-weight: 500; cursor: pointer; margin-top: 8px;
}
.btn-primary:disabled { background: #c0c4cc; cursor: not-allowed; }

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
  max-width: 480px;
  margin: 0 auto;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}
</style>
